import { FormEvent, useEffect, useMemo, useState } from "react"
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNotifications } from "../common/notification/NotificationContext"
import { FeedbackControllerService } from "../../services/FeedbackControllerService"
import { SendFeedbackRequest } from "../../models/SendFeedbackRequest"
import { FeedbackResponse } from "../../models/FeedbackResponse"
import { RootState } from "../../app/store"

export function Feedback() {
  const { t } = useTranslation()
  const { addNotification } = useNotifications()
  const [text, setText] = useState("")
  const [category, setCategory] = useState<SendFeedbackRequest.category>(SendFeedbackRequest.category.GENERAL)
  const [isPublic, setIsPublic] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [feedbacks, setFeedbacks] = useState<FeedbackResponse[]>([])
  const [feedbackError, setFeedbackError] = useState("")
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(true)

  const currentUser = useSelector((state: RootState) => state.user.user)

  const loadFeedbacks = async () => {
    setIsFeedbackLoading(true)
    setFeedbackError("")

    if (!currentUser?.id) {
      setFeedbacks([])
      setIsFeedbackLoading(false)
      return
    }

    try {
      const allFeedback = await FeedbackControllerService.getFeedbacks()
      const ownFeedback = allFeedback.filter((item) => item.authorId === currentUser.id)
      const publicFeedback = allFeedback.filter(
        (item) => item.isPublic === true && item.authorId !== currentUser.id
      )
      setFeedbacks([...ownFeedback, ...publicFeedback])
    } catch (error) {
      const fallbackMessage =
        typeof error === "string"
          ? error
          : error instanceof Error
            ? error.message
            : t("feedback.list_failed")

      setFeedbackError(fallbackMessage)
      setFeedbacks([])
    } finally {
      setIsFeedbackLoading(false)
    }
  }

  useEffect(() => {
    loadFeedbacks()
  }, [currentUser?.id])

  const clearForm = () => {
    setText("")
    setCategory(SendFeedbackRequest.category.GENERAL)
    setIsPublic(false)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const trimmedText = text.trim()

    if (trimmedText.length === 0) {
      return addNotification(t("feedback.text_required"), "warning")
    }

    setIsSubmitting(true)

    try {
      await FeedbackControllerService.sendFeedback({
        text: trimmedText,
        category,
        status: SendFeedbackRequest.status.OPEN,
        isPublic,
      })

      addNotification(t("feedback.success"), "info")
      clearForm()
      await loadFeedbacks()
    } catch (error) {
      const fallbackMessage =
        typeof error === "string"
          ? error
          : error instanceof Error
            ? error.message
            : t("feedback.error")

      addNotification(fallbackMessage, "warning")
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatDate = (dateValue?: string) => {
    if (!dateValue) {
      return "--"
    }

    return new Date(dateValue).toLocaleString()
  }

  const formatCategory = (value?: SendFeedbackRequest.category) => {
    const key =
      value === SendFeedbackRequest.category.GENERAL
        ? "general"
        : value === SendFeedbackRequest.category.BUG
          ? "bug"
          : value === SendFeedbackRequest.category.FEATURE
            ? "feature"
            : value === SendFeedbackRequest.category.IMPROVEMENT
              ? "improvement"
              : "other"

    return t(`feedback.categories.${key}`)
  }

  const formatStatus = (value?: FeedbackResponse.status) => {
    if (value === FeedbackResponse.status.OPEN) {
      return t("feedback.status_open")
    }
    if (value === FeedbackResponse.status.IN_PROGRESS) {
      return t("feedback.status_in_progress")
    }
    if (value === FeedbackResponse.status.RESOLVED) {
      return t("feedback.status_resolved")
    }
    if (value === FeedbackResponse.status.ARCHIVED) {
      return t("feedback.status_archived")
    }

    return "--"
  }

  const groupedFeedbacks = useMemo(() => {
    const own = feedbacks.filter((item) => item.authorId === currentUser?.id)
    const publicOnly = feedbacks.filter(
      (item) => item.isPublic === true && item.authorId !== currentUser?.id
    )

    return { own, publicOnly }
  }, [feedbacks, currentUser?.id])

  const renderTable = (entries: FeedbackResponse[]) => {
    if (entries.length === 0) {
      return null
    }

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("feedback.table_headers.date")}</TableCell>
              <TableCell>{t("feedback.table_headers.category")}</TableCell>
              <TableCell>{t("feedback.table_headers.status")}</TableCell>
              <TableCell>{t("feedback.table_headers.public")}</TableCell>
              <TableCell>{t("feedback.table_headers.text")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{formatDate(item.date)}</TableCell>
                <TableCell>{formatCategory(item.category)}</TableCell>
                <TableCell>{formatStatus(item.status)}</TableCell>
                <TableCell>{item.isPublic ? t("feedback.yes") : t("feedback.no")}</TableCell>
                <TableCell sx={{ maxWidth: 280, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{item.text || "--"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  return (
    <Box sx={{ backgroundColor: "#FBF7F2", minHeight: "100vh", p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1100, mx: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h4" sx={{ color: "#2E2A26", fontWeight: 700 }}>
          {t("feedback.title")}
        </Typography>
        <Typography sx={{ color: "#6F675F" }}>{t("feedback.subtitle")}</Typography>

        <Paper
          sx={{
            borderRadius: 3,
            border: "1px solid #E6E1DB",
            p: { xs: 2, md: 3 },
            backgroundColor: "#FFFFFF",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
          component="form"
          onSubmit={handleSubmit}
        >
          <Typography variant="h6" sx={{ color: "#2E2A26" }}>
            {t("feedback.form_title")}
          </Typography>
          <TextField
            label={t("feedback.text_label")}
            value={text}
            onChange={(event) => setText(event.target.value)}
            multiline
            minRows={5}
            maxRows={12}
            fullWidth
            required
            helperText={t("feedback.text_helper")}
          />

          <RadioGroup
            row
            value={category}
            onChange={(event) => setCategory(event.target.value as SendFeedbackRequest.category)}
            sx={{ gap: 1 }}
          >
            <FormControlLabel
              value={SendFeedbackRequest.category.BUG}
              control={<Radio />}
              label={t("feedback.categories.bug")}
            />
            <FormControlLabel
              value={SendFeedbackRequest.category.FEATURE}
              control={<Radio />}
              label={t("feedback.categories.feature")}
            />
            <FormControlLabel
              value={SendFeedbackRequest.category.IMPROVEMENT}
              control={<Radio />}
              label={t("feedback.categories.improvement")}
            />
            <FormControlLabel
              value={SendFeedbackRequest.category.OTHER}
              control={<Radio />}
              label={t("feedback.categories.other")}
            />
            <FormControlLabel
              value={SendFeedbackRequest.category.GENERAL}
              control={<Radio />}
              label={t("feedback.categories.general")}
            />
          </RadioGroup>

          <FormControlLabel
            control={<Checkbox checked={isPublic} onChange={(event) => setIsPublic(event.target.checked)} />}
            label={t("feedback.public")}
            sx={{ color: "#4E463E" }}
          />

          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              alignSelf: "flex-start",
              backgroundColor: "#7A4A2E",
              borderRadius: 2,
              px: 3,
              textTransform: "none",
            }}
          >
            {isSubmitting ? t("feedback.submitting") : t("feedback.submit")}
          </Button>
        </Paper>

        <Paper
          sx={{
            borderRadius: 3,
            border: "1px solid #E6E1DB",
            p: { xs: 2, md: 3 },
            backgroundColor: "#FFFFFF",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography variant="h6" sx={{ color: "#2E2A26", mb: 1 }}>
            {t("feedback.list_title")}
          </Typography>

          {feedbackError ? (
            <Typography color="error">{feedbackError}</Typography>
          ) : isFeedbackLoading ? (
            <Typography sx={{ color: "#6F675F" }}>{t("feedback.list_loading")}</Typography>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ color: "#2E2A26", mt: 1, mb: 1 }}>
                {t("feedback.my_feedbacks_title")}
              </Typography>
              {groupedFeedbacks.own.length > 0 ? (
                renderTable(groupedFeedbacks.own)
              ) : (
                <Typography sx={{ color: "#6F675F", mb: 1 }}>{t("feedback.list_empty_own")}</Typography>
              )}

              <Typography variant="subtitle2" sx={{ color: "#2E2A26", mt: 2, mb: 1 }}>
                {t("feedback.public_feedbacks_title")}
              </Typography>
              {groupedFeedbacks.publicOnly.length > 0 ? (
                renderTable(groupedFeedbacks.publicOnly)
              ) : (
                <Typography sx={{ color: "#6F675F" }}>{t("feedback.list_empty_public")}</Typography>
              )}
            </>
          )}
        </Paper>
      </Box>
    </Box>
  )
}
